import { PageHeader, FilterBar, DataTable, LoadingState, EmptyState } from '../components/AdminPageUI';
import SummaryStrip, { SummaryMetric } from '../components/SummaryStrip';
import React, { useState, useEffect } from 'react';
import { Typography, Button, Container, Grid, Card, CardContent, Table, TableBody, TableCell, TableHead, TableRow, Paper, Chip, Alert, TextField } from '@mui/material';
import { Warning as WarningIcon, CheckCircle as CheckCircleIcon, DirectionsBus as BusIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import api from '../utils/Api';
import { toast } from 'react-toastify';

export default function TransportDashboard() {
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [missingDropOffs, setMissingDropOffs] = useState([]);
  const [dailyAttendance, setDailyAttendance] = useState([]);
  const [stats, setStats] = useState({ totalBoarded: 0, totalExited: 0, completionRate: 0 });

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      
      const [missingRes, dailyRes] = await Promise.all([
        api.get('/transport/missing-dropoffs', { params: { date: formattedDate } }),
        api.get('/transport/daily-attendance', { params: { date: formattedDate } })
      ]);

      const missing = missingRes.data.missing || [];
      const records = dailyRes.data.records || [];
      
      setMissingDropOffs(missing);
      setDailyAttendance(records);
      
      const boarded = records.filter(r => r.boarded).length;
      const exited = records.filter(r => r.exited).length;
      const rate = boarded > 0 ? Math.round((exited / boarded) * 100) : 0;
      
      setStats({
        totalBoarded: boarded,
        totalExited: exited,
        completionRate: rate
      });

    } catch (err) {
      toast.error('Failed to load transport dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [selectedDate]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <PageHeader title={<>Transport Analytics</>} description={<>Monitor boarding, drop-offs, and transport activity.</>}></PageHeader>
      <SummaryStrip container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 4 }}   >
          <SummaryMetric elevation={2} sx={{ bgcolor: 'primary.50' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" gutterBottom>Total Boarded Today</Typography>
              <Typography variant="h3" fontWeight="bold" color="primary.main">
                {stats.totalBoarded}
              </Typography>
            </CardContent>
          </SummaryMetric>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}   >
          <SummaryMetric elevation={2} sx={{ bgcolor: 'success.50' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" gutterBottom>Total Successfully Dropped Off</Typography>
              <Typography variant="h3" fontWeight="bold" color="success.main">
                {stats.totalExited}
              </Typography>
            </CardContent>
          </SummaryMetric>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}   >
          <SummaryMetric elevation={2} sx={{ bgcolor: stats.completionRate < 100 && stats.totalBoarded > 0 ? 'warning.50' : 'info.50' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" gutterBottom>Drop-off Completion Rate</Typography>
              <Typography variant="h3" fontWeight="bold" color={stats.completionRate < 100 && stats.totalBoarded > 0 ? 'warning.main' : 'info.main'}>
                {stats.completionRate}%
              </Typography>
            </CardContent>
          </SummaryMetric>
        </Grid>
      </SummaryStrip>
      <FilterBar>
<DatePicker
            label="View Date"
            value={selectedDate}
            onChange={(newValue) => setSelectedDate(newValue)}
            renderInput={(params) => <TextField {...params} size="small" fullWidth />}
          />
      </FilterBar>

      {/* Stats Cards */}
      

      {/* Critical Alerts: Missing Drop-offs */}
      {missingDropOffs.length > 0 && (
        <Alert severity="error" icon={<WarningIcon fontSize="large" />} sx={{ mb: 4, '& .MuiAlert-message': { width: '100%' } }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>Critical Safety Alert: Missing Drop-Offs Detected!</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The following students boarded a bus today but have NOT been marked as exited. Please contact the assigned teacher immediately to verify their location.
          </Typography>
          
          <DataTable component={Paper} elevation={0} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Student</TableCell>
                  <TableCell>Route</TableCell>
                  <TableCell>Teacher</TableCell>
                  <TableCell>Boarded At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
{missingDropOffs.length === 0 && <TableRow><TableCell colSpan={100}><EmptyState title="No records found" description="Records will appear here when available. Check your selected filters." /></TableCell></TableRow>}

                {missingDropOffs.map(missing => (
                  <TableRow key={missing._id} sx={{ bgcolor: 'error.lighter' }}>
                    <TableCell fontWeight="bold">{missing.student?.name}</TableCell>
                    <TableCell>{missing.route?.name}</TableCell>
                    <TableCell>{missing.assignment?.teacher?.name || 'Unknown'}</TableCell>
                    <TableCell>{missing.boardedAt ? new Date(missing.boardedAt).toLocaleTimeString() : 'Unknown'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DataTable>
        </Alert>
      )}

      {/* Full Daily Log */}
      <Card elevation={2}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusIcon /> Full Daily Transit Log
          </Typography>
          
          {loading ? (
            <LoadingState />
          ) : (
            <DataTable>
              <Table>
                <TableHead sx={{ bgcolor: 'background.default' }}>
                  <TableRow>
                    <TableCell>Student</TableCell>
                    <TableCell>Route</TableCell>
                    <TableCell>Boarding Status</TableCell>
                    <TableCell>Exit Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dailyAttendance.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">No transport activity recorded for this date.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    dailyAttendance.map(record => (
                      <TableRow key={record._id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">{record.student?.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{record.student?.admissionNumber}</Typography>
                        </TableCell>
                        <TableCell>{record.route?.name}</TableCell>
                        <TableCell>
                          {record.boarded ? 
                            <Chip size="small" color="primary" label={`Boarded ${record.boardedAt ? new Date(record.boardedAt).toLocaleTimeString() : ''}`} /> 
                            : <Chip size="small" variant="outlined" label="Not Boarded" />}
                        </TableCell>
                        <TableCell>
                          {record.exited ? 
                            <Chip size="small" color="success" icon={<CheckCircleIcon />} label={`Dropped off at ${record.exitStop}`} /> 
                            : (record.boarded ? <Chip size="small" color="error" label="Still on Bus" /> : <Chip size="small" variant="outlined" label="N/A" />)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </DataTable>
          )}
        </CardContent>
      </Card>
      
    </Container>
  );
}
